import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  OutlineButton,
  Badge,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { Notification } from '../../types';
import { formatDateTime } from '../../utils/dateUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: 1rem;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 600;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
`;

const NotificationCard = styled.div<{ isRead: boolean }>`
  background: ${props => props.isRead ? 'white' : 'rgba(246, 139, 33, 0.05)'};
  border-left: 4px solid ${props => props.isRead ? 'var(--light-gray)' : 'var(--primary-color)'};
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const NotificationTitle = styled.h4<{ isRead: boolean }>`
  margin: 0;
  color: ${props => props.isRead ? 'var(--dark-gray)' : 'var(--secondary-color)'};
  font-weight: ${props => props.isRead ? '500' : '600'};
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
  color: var(--medium-gray);
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const NotificationContent = styled.div`
  color: var(--dark-gray);
  line-height: 1.5;
  margin-bottom: 0.5rem;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const TypeBadge = styled(Badge)`
  font-size: 0.75rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--dark-gray);
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const NotificationsList: React.FC = () => {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useNotifications();
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [readFilter, setReadFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  const itemsPerPage = 10;

  useEffect(() => {
    if (user && notifications.length > 0) {
      setFilteredNotifications(notifications);
    }
  }, [user, notifications]);

  useEffect(() => {
    let result = [...notifications];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        notification => 
          notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          notification.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (typeFilter) {
      result = result.filter(notification => notification.type === typeFilter);
    }
    
    // Apply read status filter
    if (readFilter) {
      result = result.filter(notification => 
        readFilter === 'read' ? notification.read : !notification.read
      );
    }
    
    // Sort by date (newest first)
    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    setFilteredNotifications(result);
    setCurrentPage(1);
  }, [notifications, searchTerm, typeFilter, readFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNotifications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getTypeVariant = (type: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (type) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Approval Required': return 'warning';
      case 'Disbursement': return 'info';
      case 'Assignment': return 'primary';
      case 'Deadline': return 'danger';
      default: return 'secondary';
    }
  };



  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setError('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setError('Failed to mark all notifications as read');
    }
  };

  // Calculate stats
  const totalNotifications = notifications.length;
  const approvalRequiredCount = notifications.filter(n => n.type === 'Approval Required').length;
  const todayCount = notifications.filter(n => {
    const today = new Date();
    const notificationDate = new Date(n.createdAt);
    return notificationDate.toDateString() === today.toDateString();
  }).length;

  // Get unique types for filters
  const uniqueTypes = Array.from(new Set(notifications.map(n => n.type)));

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Notifications</h1>
        <FlexContainer gap="0.5rem">
          <Button onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
            Mark All as Read
          </Button>
          <OutlineButton onClick={() => window.location.pathname !== '/notifications' && window.location.assign('/notifications')}>
            View All
          </OutlineButton>
          <OutlineButton>
            Settings
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <div className="stat-number">{totalNotifications}</div>
          <div className="stat-label">Total Notifications</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{unreadCount}</div>
          <div className="stat-label">Unread</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{approvalRequiredCount}</div>
          <div className="stat-label">Approval Required</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{todayCount}</div>
          <div className="stat-label">Today</div>
        </StatCard>
      </StatsContainer>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search notifications..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>Type:</label>
          <Select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {uniqueTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={readFilter} 
            onChange={(e) => setReadFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </Select>
        </FilterGroup>

        <Button onClick={() => {
          setSearchTerm('');
          setTypeFilter('');
          setReadFilter('');
        }}>
          Reset Filters
        </Button>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>Notifications ({filteredNotifications.length})</h3>
        </CardHeader>
        <CardBody>
          {currentItems.length > 0 ? (
            <div>
              {currentItems.map((notification) => (
                <NotificationCard key={notification.id} isRead={notification.read}>
                  <NotificationHeader>
                    <NotificationTitle isRead={notification.read}>
                      {notification.title}
                    </NotificationTitle>
                    <TypeBadge variant={getTypeVariant(notification.type)}>
                      {notification.type}
                    </TypeBadge>
                  </NotificationHeader>
                  
                  <NotificationMeta>
                    <span>{formatDateTime(notification.createdAt)}</span>
                    {notification.data?.module && (
                      <span>• {notification.data.module}</span>
                    )}
                    {notification.data?.relatedId && (
                      <span>• ID: {notification.data.relatedId}</span>
                    )}
                  </NotificationMeta>
                  
                  <NotificationContent>
                    {notification.message}
                    
                    {/* Show detailed information for account creation notifications */}
                    {notification.type === 'System Alert' && notification.title.includes('Account') && notification.data?.userDetails && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.9rem' }}>
                        <strong>Account Details:</strong>
                        <div>Name: {notification.data.userDetails.name}</div>
                        <div>Email: {notification.data.userDetails.email}</div>
                        <div>Role: {notification.data.userDetails.role}</div>
                        <div>Created by: {notification.data.createdBy}</div>
                        <div>Created at: {formatDateTime(notification.data.createdAt)}</div>
                      </div>
                    )}
                  </NotificationContent>
                  
                  <NotificationActions>
                    {!notification.read && (
                      <OutlineButton onClick={() => handleMarkAsRead(notification.id)}>
                        Mark as Read
                      </OutlineButton>
                    )}
                    
                    {notification.data?.actionUrl && !notification.title.includes('Account') && (
                      <Button as={Link} to={notification.data.actionUrl}>
                        View Details
                      </Button>
                    )}
                    
                    {notification.title.includes('Account') && (
                      <Button onClick={() => window.open(notification.data?.actionUrl, '_blank')}>
                        Activate Account
                      </Button>
                    )}
                  </NotificationActions>
                </NotificationCard>
              ))}
            </div>
          ) : (
            <EmptyState>
              <div className="icon">🔔</div>
              <h3>No notifications found</h3>
              <p>You're all caught up! No notifications match your current filters.</p>
            </EmptyState>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &raquo;
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </Card>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default NotificationsList; 